import React, { FC, memo } from 'react'
import { cn } from 'ethcss'
import { TemplateEditorElementView, elementDisplayStyle } from '../elements/views/TemplateEditorElementView'
import { listItemHeight, listItemSlotWidth } from '../elements/placementMetrics'
import { TemplateEditorInlineEditApi } from '../hooks/document/useTemplateEditorInlineEdit'
import { EditorTheme } from '../templateEditor.theme'
import { elementHitAreaStyle, toDisplaySize } from '../templateEditor.utils'
import {
    CatalogFileItem,
    PlaceModeOptions,
    ResizeHandle as ResizeHandleType,
    TemplateElement,
} from '../templateEditor.types'
import { TemplateEditorListChrome, TemplateEditorTableChrome } from './templateEditor__elementChrome'
import { TemplateEditorResizeHandle } from './templateEditor__resizeHandle'
import styles from './templateEditor__canvasElement.jcss'

const HANDLES: ResizeHandleType[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

export type TemplateEditorCanvasElementActions = {
    replaceImage: (elementId: string, payload: PlaceModeOptions) => void
    catalogPayloadFromItem: (item: CatalogFileItem) => PlaceModeOptions | null
    insertTableRow: (elementId: string, index: number) => void
    insertTableCol: (elementId: string, index: number) => void
    resizeTableRow: (elementId: string, borderIndex: number, deltaLogical: number) => void
    resizeTableCol: (elementId: string, borderIndex: number, deltaLogical: number) => void
    onTableResizeGestureStart: () => void
    insertListItem: (elementId: string, index: number) => void
}

type TemplateEditorCanvasElementProps = {
    element: TemplateElement
    selected: boolean
    showHandles?: boolean
    theme: EditorTheme
    inlineEdit: TemplateEditorInlineEditApi
    disabled?: boolean
    pointerScale?: number
    actions: TemplateEditorCanvasElementActions
    onSelectMove: (event: React.MouseEvent) => void
    onResize: (handle: ResizeHandleType, event: React.MouseEvent) => void
    onContextMenu?: (event: React.MouseEvent) => void
}

const allowImageDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
}

const applyCatalogImageDrop = (
    event: React.DragEvent,
    elementId: string,
    actions: TemplateEditorCanvasElementActions
) => {
    event.preventDefault()
    event.stopPropagation()
    const raw = event.dataTransfer.getData('application/json')
    if (!raw) {
        return
    }
    try {
        const item = JSON.parse(raw) as CatalogFileItem
        const payload = actions.catalogPayloadFromItem(item)
        if (payload) {
            actions.replaceImage(elementId, payload)
        }
    } catch {
        // ignore invalid payload
    }
}

export const TemplateEditorCanvasElement: FC<TemplateEditorCanvasElementProps> = memo(
    ({
        element,
        selected,
        showHandles,
        theme,
        inlineEdit,
        disabled,
        pointerScale,
        actions,
        onSelectMove,
        onResize,
        onContextMenu,
    }) => {
        const isGroup = element.type === 'group'
        const locked = Boolean(element.locked)
        const showChrome = selected && showHandles && !locked && !disabled
        const id = element.id
        const isImage = element.type === 'image'

        return (
            <div
                className={cn(styles.el, selected && styles.selected, isGroup && styles.group, locked && styles.locked)}
                style={{
                    ...elementDisplayStyle(element, theme.displayScale),
                    pointerEvents: disabled ? 'none' : undefined,
                }}
                onContextMenu={onContextMenu}
                onDragOver={isImage ? allowImageDrop : undefined}
                onDrop={isImage ? (event) => applyCatalogImageDrop(event, id, actions) : undefined}
            >
                {!isGroup && (
                    <div
                        className={styles.hitArea}
                        style={elementHitAreaStyle(element.w, element.h, theme.displayScale, theme.minHitSize)}
                        onMouseDown={(event) => {
                            if (inlineEdit.inlineEdit) {
                                return
                            }
                            onSelectMove(event)
                        }}
                    />
                )}
                <div
                    className={styles.elContent}
                    style={
                        element.type === 'shape' && element.shapeKind === 'line' ? { overflow: 'visible' } : undefined
                    }
                    onMouseDown={(event) => {
                        if (inlineEdit.inlineEdit) {
                            return
                        }
                        onSelectMove(event)
                    }}
                >
                    <TemplateEditorElementView element={element} theme={theme} inlineEdit={inlineEdit} />
                </div>
                {showChrome && element.type === 'table' && (
                    <TemplateEditorTableChrome
                        element={element}
                        scale={theme.displayScale}
                        pointerScale={pointerScale ?? theme.displayScale}
                        onInsertRow={(index) => actions.insertTableRow(id, index)}
                        onInsertCol={(index) => actions.insertTableCol(id, index)}
                        onResizeRow={(borderIndex, delta) => actions.resizeTableRow(id, borderIndex, delta)}
                        onResizeCol={(borderIndex, delta) => actions.resizeTableCol(id, borderIndex, delta)}
                        onResizeGestureStart={actions.onTableResizeGestureStart}
                    />
                )}
                {showChrome && element.type === 'list' && (
                    <TemplateEditorListChrome
                        element={element}
                        scale={theme.displayScale}
                        itemStep={toDisplaySize(
                            element.orientation === 'horizontal'
                                ? listItemSlotWidth(theme) + element.gap
                                : listItemHeight(
                                      { fontSize: element.fontSize, lineHeight: 1.5, gap: element.gap },
                                      theme
                                  ),
                            theme.displayScale
                        )}
                        onInsertItem={(index) => actions.insertListItem(id, index)}
                    />
                )}
                {selected &&
                    showHandles &&
                    !locked &&
                    HANDLES.map((handle) => (
                        <TemplateEditorResizeHandle
                            key={handle}
                            handle={handle}
                            onMouseDown={(event) => onResize(handle, event)}
                        />
                    ))}
            </div>
        )
    },
    (prev, next) =>
        prev.element === next.element &&
        prev.selected === next.selected &&
        prev.showHandles === next.showHandles &&
        prev.disabled === next.disabled &&
        prev.pointerScale === next.pointerScale &&
        prev.theme === next.theme &&
        prev.inlineEdit === next.inlineEdit &&
        prev.actions === next.actions
)
